import { destroyEvents } from '@Common/Core/Destroyable';
import { treeIterator } from '@Common/Helpers/CollectionUtils';
import * as commonUtils from '@Common/Helpers/CommonUtils';
import { comparers } from '@Common/Helpers/Comparers';
import { selectMode } from './BaseMenu';

export class TreeStore {
    private _param: any;
    private _items: any[];
    private _nodes: any[];
    private _currentSelectedItems: any[];
    private _currentCheckedItems: any[];
    private _initCheckedItems: any[];
    private _initSelectedItems: any[];
    private _shiftNode: any;
    private _goToTop: boolean;
    private _fakeIdIndex: { value: number };
    private _onSelectionChanged: (old, newItems, param?: { removePopup: boolean }) => void;
    private _onCheckedChanged: (old, newItems) => void;
    private _onAdded: (node, index) => void;
    private _lastShiftEvent: { isPrev: boolean; node: any };

    constructor(param) {
        this._param = {
            ...{
                checkable: false,
                selectMode: selectMode.simple,
                highlightAllSelectedWay: false,
                comparer: undefined,
                allowSorting: false,
                sortable: true,
                visibilityComparer: null,
                idPropertyName: 'id',
                namePropertyName: 'name'
            },
            ...param
        };

        this._items = [];
        this._nodes = [];
        this._currentSelectedItems = [];
        this._currentCheckedItems = [];
        this._initCheckedItems = [];
        this._initSelectedItems = [];
        this._shiftNode = null;
        this._goToTop = false;
        this._fakeIdIndex = { value: 0 };

        if (!this._param.comparer) {
            const { idPropertyName } = this._param;
            this._param.comparer = (a, b) => {
                if (_.hasIn(a, idPropertyName) && _.hasIn(b, idPropertyName)) {
                    if (a[idPropertyName] == b[idPropertyName]) {
                        return 0;
                    } else if (a[idPropertyName] > b[idPropertyName]) {
                        return 1;
                    } else {
                        return -1;
                    }
                } else if (a && a._innerId !== undefined && b && b._innerId !== undefined) {
                    if (a._innerId == b._innerId) {
                        return 0;
                    } else if (a._innerId > b._innerId) {
                        return 1;
                    } else {
                        return -1;
                    }
                }
            };
        }

        this._onSelectionChanged = $.noop;
        this._onCheckedChanged = $.noop;
        this._onAdded = $.noop;
    }

    private _sortAll(items?, nodes?) {
        const self = this;

        if (items == undefined) {
            items = self._items;
        }
        if (nodes == undefined) {
            nodes = self._nodes;
        }

        function sortAll(items, nodes) {
            if (self._param.folderOnTop) {
                const parentToTop = (items, nodeParam?) => {
                    const folders = [];
                    const notFolders = [];

                    _(items).forEach(function (item) {
                        let i;
                        if (nodeParam && nodeParam.isNode) {
                            i = item.data;
                        } else {
                            i = item;
                        }

                        if (i.isFolder === true) {
                            folders.push(item);
                        } else {
                            notFolders.push(item);
                        }
                    });

                    if (self._param.sortable) {
                        if (nodeParam && nodeParam.isNode) {
                            self._param.sort
                                ? folders.sort(
                                      function (a, b) {
                                          return self._param.sort(a.data, b.data);
                                      }.bind(self)
                                  )
                                : folders.sort(self._sortNode(self._param.namePropertyName));

                            self._param.sort
                                ? notFolders.sort(
                                      function (a, b) {
                                          return self._param.sort(a.data, b.data);
                                      }.bind(self)
                                  )
                                : notFolders.sort(self._sortNode(self._param.namePropertyName));
                        } else {
                            self._param.sort ? folders.sort(self._param.sort) : folders.sort(self._sortItem(self._param.namePropertyName));

                            self._param.sort ? notFolders.sort(self._param.sort) : notFolders.sort(self._sortItem(self._param.namePropertyName));
                        }
                    }

                    return folders.concat(notFolders);
                };

                items = parentToTop(items);
                if (nodes && nodes.length) {
                    nodes = parentToTop(nodes, { isNode: true });
                }
            } else if (self._param.sortable) {
                self._param.sort ? items.sort(self._param.sort) : items.sort(self._sortItem(self._param.namePropertyName));

                if (nodes && nodes.length) {
                    self._param.sort
                        ? nodes.sort(
                              function (a, b) {
                                  return self._param.sort(a.data, b.data);
                              }.bind(self)
                          )
                        : nodes.sort(self._sortNode(self._param.namePropertyName));
                }
            }

            return [items, nodes];
        }

        const result = sortAll(items, nodes);
        this._items = result[0] || this._items;
        if (nodes && nodes.length) {
            this._nodes = result[1] || this._nodes;
        }
    }

    private _sortItem(prop) {
        return function (a, b) {
            return comparers.stringComparer(a[prop], b[prop]);
        };
    }

    private _sortNode(prop) {
        return function (a, b) {
            return comparers.stringComparer(a.data[prop], b.data[prop]);
        };
    }

    private _removeNode(data) {
        this._currentSelectedItems = this._getSelectedItems();
        this._param.checkable && (this._currentCheckedItems = this._getCheckedItems());

        function setNodes(nodes) {
            return _(nodes)
                .filter(
                    function (i) {
                        if (i._children && i.children.length) {
                            i._children = setNodes(i._children);
                        }

                        return i.data != data;
                    }.bind(this)
                )
                .value();
        }

        function setItems(items) {
            _.pull(items, data).forEach(function (i) {
                if (i.children && i.children.length) {
                    setItems(i.children);
                }
            });
        }

        this._nodes = setNodes(this._nodes);
        setItems(this._items);

        this.selectedItems(this._getSelectedItems());
        this._param.checkable && this.checkedItems(this._getCheckedItems());
    }

    private _deselectAll() {
        function deselect(items) {
            _(items).forEach(function (i) {
                i.selected && i.select(false, { stopEvent: true });
                i.data.selected = false;

                if (i.children && i.children.length) {
                    deselect(i.children);
                }
            });
        }

        deselect(this._nodes);
        this._initSelectedItems = [];
    }

    private _uncheckAll() {
        function uncheck(items) {
            _(items).forEach(function (i) {
                if (i.checked) {
                    i.check(false, { stopEvent: true });
                }

                if (i.children && i.children.length) {
                    uncheck(i.children);
                }
            });
        }

        uncheck(this._nodes);
        this._initCheckedItems = [];
    }

    private _createNode(item) {
        if (item[this._param.idPropertyName] === undefined || item[this._param.idPropertyName] === null) {
            item._innerId = this._fakeIdIndex.value++;
            this._param.fakeIdIndex = this._fakeIdIndex;
        }

        const n = new TreeNode(item, this._param);
        n._onRemove = function (data) {
            this._removeNode(data);
        }.bind(this);
        n._onSelect = function (node, value, selectParam) {
            const data = node.data;
            const oldSelectedItems = selectParam && selectParam.stopEvent ? [] : this._getSelectedItems();

            // ctrl pressed
            const isCtrlPressed =
                !this._param.ignoreKeys &&
                (this._param.selectMode == selectMode.multiple || !this._param.isSimpleCheckMode) &&
                selectParam &&
                selectParam.event &&
                selectParam.event.ctrlKey;

            // shift pressed
            const isShiftPressed =
                !this._param.ignoreKeys &&
                (this._param.selectMode == selectMode.multiple || !this._param.isSimpleCheckMode) &&
                selectParam &&
                selectParam.event &&
                selectParam.event.shiftKey;

            if (selectParam === undefined || (selectParam.deselectAll !== false && !isShiftPressed && !isCtrlPressed && value)) {
                this._deselectAll();
            }

            if (value == true && isShiftPressed) {
                const selectedItems = oldSelectedItems;
                selectedItems.length > 0 && this._deselectAll();

                const oldNode = this.findNodeByItem(selectedItems[0]);

                // shift key can be used only on same level
                if (oldNode.level == node.level) {
                    let levelNodes;
                    if (oldNode.level == 0) {
                        levelNodes = this._nodes;
                    } else {
                        levelNodes = oldNode.parent.children;
                    }

                    let ind1;
                    let ind2;
                    _(levelNodes).forEach(function (i, index) {
                        if (i == oldNode) {
                            ind1 = index;
                        }
                        if (i == node) {
                            ind2 = index;
                        }

                        if (ind1 && ind2) {
                            return false;
                        }
                    });

                    const toBottom = ind1 < ind2;

                    _(levelNodes).forEach(
                        function (i, index) {
                            if (toBottom) {
                                if (index >= ind1 && index <= ind2) {
                                    levelNodes[index].select(
                                        true,
                                        $.extend(selectParam, {
                                            stopEvent: true,
                                            stopAffectParent: true,
                                            stopAffectChildren: true,
                                            event: null,
                                            deselectAll: false
                                        })
                                    );
                                }
                            } else {
                                if (index >= ind2 && index <= ind1) {
                                    levelNodes[index].select(
                                        true,
                                        $.extend(selectParam, {
                                            stopEvent: true,
                                            stopAffectParent: true,
                                            stopAffectChildren: true,
                                            event: null,
                                            deselectAll: false
                                        })
                                    );
                                }
                            }
                        }.bind(this)
                    );
                } else {
                }
            }

            data.selected = value;

            if (!value && this._initSelectedItems && this._initSelectedItems.length) {
                const index = this._binaryIndexOf(this._initSelectedItems, data);
                if (index > -1) {
                    this._initSelectedItems.splice(index, 1);
                }
            }

            function setItems(items) {
                _(items).forEach(function (i, index) {
                    if (i.children && i.children.length) {
                        setItems(i.children);
                    }

                    if (i == data) {
                        items[index].selected = value;
                        return false;
                    }
                });
            }

            setItems(this._items);

            if (!selectParam || !selectParam.stopEvent) {
                this._currentSelectedItems = this._getSelectedItems();

                (oldSelectedItems.length != this._currentSelectedItems.length ||
                    _(oldSelectedItems).some(
                        function (i) {
                            return _(this._currentSelectedItems).some(function (n) {
                                return n != i;
                            });
                        }.bind(this)
                    )) &&
                    this._onSelectionChanged(oldSelectedItems, this._currentSelectedItems, {
                        removePopup: this._currentSelectedItems.length
                            ? !(this._currentSelectedItems[0].children && this._currentSelectedItems[0].children.length)
                            : false
                    });
            }
        }.bind(this);

        n._onCheck = function (data, value, checkParam) {
            let oldCheckedItems;

            if (!checkParam || !checkParam.stopEvent) {
                oldCheckedItems = (checkParam && checkParam.oldCheckedItems) || this._getCheckedItems();
            }

            if (this._param.isSimpleCheckMode && value) {
                this._uncheckAll();
            }

            data.checked = value;

            if (!value && this._initCheckedItems && this._initCheckedItems.length) {
                const index = this._binaryIndexOf(this._initCheckedItems, data);
                if (index > -1) {
                    this._initCheckedItems.splice(index, 1);
                }
            }

            if (!checkParam || !checkParam.stopEvent) {
                this._currentCheckedItems = this._getCheckedItems();

                (oldCheckedItems.length != this._currentCheckedItems.length ||
                    _(oldCheckedItems).some(
                        function (i) {
                            return _(this._currentCheckedItems).some(function (n) {
                                return n !== i;
                            });
                        }.bind(this)
                    )) &&
                    this._onCheckedChanged(oldCheckedItems, this._currentCheckedItems);
            }
        }.bind(this);

        n._onPreCheck = function () {
            return this._getCheckedItems();
        }.bind(this);

        n._onUpdate = function (data, value) {
            const a = this._getCheckedItems();
            this.checkedItems(a);

            const s = this._getSelectedItems();
            this.selectedItems(s);

            this._sortAll();
        }.bind(this);

        return n;
    }

    private _getSelectedItems() {
        const results = [];

        function find(items) {
            _(items).forEach(function (i) {
                if (i.selected === true) {
                    results.push(i);
                }

                if (i.children && i.children.length) {
                    find(i.children);
                }
            });
        }

        find(this._items);

        if (this._initSelectedItems && this._initSelectedItems.length) {
            return _.union(results, this._initSelectedItems);
        } else {
            return results;
        }
    }

    private _getCheckedItems() {
        const results = [];

        function find(items) {
            _(items).forEach(function (i) {
                if (i.checked === true) {
                    results.push(i);
                }

                if (i.children && i.children.length) {
                    find(i.children);
                }
            });
        }

        find(this._items);

        if (this._initCheckedItems && this._initCheckedItems.length) {
            return _(results)
                .union(this._initCheckedItems)
                .uniqBy(function (item) {
                    return item.id;
                })
                .orderBy(function (item) {
                    return item.id;
                })
                .value();
        } else {
            return results;
        }
    }

    private _binaryIndexOf(items, searchElement, customComparer) {
        let self = this;
        let minIndex = 0;
        let maxIndex = items.length - 1;
        let currentIndex;
        let currentElement;
        while (minIndex <= maxIndex) {
            currentIndex = ((minIndex + maxIndex) / 2) | 0;
            currentElement = items[currentIndex];

            // if the same object
            if (currentElement === searchElement) {
                return true;
            }

            const comparer = customComparer || self._param.comparer;

            // find by id
            if (comparer(currentElement, searchElement) == -1) {
                minIndex = currentIndex + 1;
            } else if (comparer(currentElement, searchElement) == 1) {
                maxIndex = currentIndex - 1;
            } else {
                return currentIndex;
            }
        }
        self = null;
        return -1;
    }

    items(items) {
        this._param.cloneItems && (items = commonUtils.clone(items));

        const oldSelectedItems = this._items.length || (items && items.length) ? this._getSelectedItems() : [];
        const oldCheckedItems = items && this._param.checkable ? this._getCheckedItems() : [];

        const self = this;

        if (items == undefined) {
            return this._items;
        } else if (items && items.length) {
            this._items = items;
            this._sortAll();

            this._nodes = [];
            _(this._items).forEach(
                function (item) {
                    const n = this._createNode(item);
                    this._nodes.push(n);
                }.bind(this)
            );
        } else {
            const currentSelectedItems = this._getSelectedItems();
            const currentCheckedItems = this._param.checkable ? this._getCheckedItems() : [];

            _(this._nodes).forEach(function (i) {
                i.remove();
            });
            this._nodes = [];
            this._items = [];

            this._currentSelectedItems = currentSelectedItems;
            this._currentCheckedItems = currentCheckedItems;

            this.selectedItems([]);
            this._param.checkable && this.checkedItems([]);
        }

        if (this._param.checkable) {
            const newCheckedItems = this._getCheckedItems();

            if (this._initCheckedItems && this._initCheckedItems.length && newCheckedItems.length === this._initCheckedItems.length) {
                this.checkedItems(this._initCheckedItems);
                //this._initCheckedItems = [];
            } else {
                this._currentCheckedItems = newCheckedItems;
                (oldCheckedItems.length != this._currentCheckedItems.length ||
                    _(oldCheckedItems).some(i => {
                        return _(this._currentCheckedItems).some(n => {
                            return n != i;
                        });
                    })) &&
                    this._onCheckedChanged(oldCheckedItems, this._currentCheckedItems);
            }
        }

        const newSelectedItems = this._getSelectedItems();
        if (this._initSelectedItems && this._initSelectedItems.length) {
            this.selectedItems(this._initSelectedItems);
            //this._initSelectedItems = [];
        } else {
            this._currentSelectedItems = newSelectedItems;
            (oldSelectedItems.length != this._currentSelectedItems.length ||
                _(oldSelectedItems).some(i => {
                    return _(this._currentSelectedItems).some(function (n) {
                        return n != i;
                    });
                })) &&
                this._onSelectionChanged(oldSelectedItems, this._currentSelectedItems);
        }

        return this._items;
    }

    addItems(items, addParam) {
        this._param.cloneItems && (items = commonUtils.clone(items));

        let self = this;

        !this._param.sortable && addParam && addParam.isPrepend && items.reverse();

        _(items).forEach(
            function (item) {
                const n = this._createNode(item);
                if (!this._param.sortable && addParam && addParam.isPrepend) {
                    this._nodes.unshift(n);
                    this._items.unshift(item);
                } else {
                    this._nodes.push(n);
                    this._items.push(item);
                }
            }.bind(this)
        );

        this._sortAll();

        this._currentSelectedItems = this._getSelectedItems();
        this._currentCheckedItems = this._param.checkable ? this._getCheckedItems() : null;

        this._param.checkable && this.checkedItems(this._currentCheckedItems);

        self = null;
        return items;
    }

    removeNodes(compare) {
        // remove all items
        if (!compare) {
            this.items([]);
        } else {
            removeNode(this._nodes);
        }

        function removeNode(items) {
            _(items).forEach(function (i) {
                if (compare(i.data)) {
                    i.remove();
                } else if (i.children && i.children.length) {
                    removeNode(i.children);
                }
            });
        }
    }

    selectedItems(selectedItems, customComparer?, ready?) {
        if (!selectedItems) {
            return this._getSelectedItems();
        }

        let readyHappened = false;
        const oldSelectedItems = this._currentSelectedItems;
        const newSelectedItems = [];
        let self = this;
        const comparer = customComparer || self._param.comparer;

        _(selectedItems.length ? selectedItems : this._initSelectedItems || []).forEach(
            function (i) {
                if (!i.dinamicIsLoaded && i.dynamicParentItem) {
                    const node = this.findNode(
                        function (data) {
                            return customComparer
                                ? customComparer(i.dynamicParentItem, data) == 0
                                : this._param.comparer(i.dynamicParentItem, data) == 0;
                        }.bind(this)
                    );

                    if (node && !node.hasChildren) {
                        readyHappened = true;
                        node.expand(null, ready);
                    }
                }
            }.bind(this)
        );

        function find(nodes) {
            _(nodes).forEach(function (node) {
                if (self._param.selectMode == selectMode.simple) {
                    node._selected = false;
                    node._item.selected = false;
                }

                if (node.data.children && node.data.children.length) {
                    find(node.children);
                }

                const isSelected = _.some(selectedItems, item => item === node.data || comparer(item, node.data) === 0);

                isSelected && newSelectedItems.push(node.data);
                node._selected = isSelected;
                node._item.selected = isSelected;
                node.onSelect(isSelected);
            });
        }

        find(self._nodes);

        // if oldOtem and newItems the same we don't need call onSelectionChanged
        const check =
            oldSelectedItems.length != newSelectedItems.length ||
            _(oldSelectedItems).some(
                function (i) {
                    return _(newSelectedItems).some(
                        function (n) {
                            return this._param.comparer(i, n) != 0;
                        }.bind(this)
                    );
                }.bind(this)
            );

        self = null;

        this._currentSelectedItems = newSelectedItems;

        // set this._initSelectedItems;
        if (newSelectedItems.length != selectedItems.length) {
            this._initSelectedItems = [];
            newSelectedItems.sort(this._param.comparer);
            _(selectedItems).forEach(
                function (i) {
                    const select = this._binaryIndexOf(newSelectedItems, i) == -1;
                    select && this._initSelectedItems.push(i);
                }.bind(this)
            );
        } else {
            this._initSelectedItems = [];
        }

        check && this._onSelectionChanged(oldSelectedItems, newSelectedItems, { removePopup: false });

        !readyHappened && ready && ready();

        return newSelectedItems;
    }

    checkedItems(checkedItems, customComparer?, ready?, checkParam?) {
        if (this._param.checkable) {
            // if checkedItems === undefined return current checkedItems
            if (checkedItems === undefined) {
                return this._getCheckedItems();
            }

            const oldCheckedItems = this._currentCheckedItems;
            const newCheckedItems = [];
            let self = this;
            let checkedNode = [];
            const comparer = customComparer || self._param.comparer;

            _(checkedItems).forEach(
                function (i) {
                    if (!i.dinamicIsLoaded && i.dynamicParentItem) {
                        const node = this.findNode(
                            function (data) {
                                return customComparer
                                    ? customComparer(i.dynamicParentItem, data) == 0
                                    : this._param.comparer(i.dynamicParentItem, data) == 0;
                            }.bind(this)
                        );

                        node && !node.expanded && node.expand(null, ready);
                    }
                }.bind(this)
            );

            const find = nodes => {
                _(nodes).forEach(function (node) {
                    // go and check node childrens
                    node.data.children && node.data.children.length && find(node.children);

                    const isChecked = _.some(checkedItems, item => item === node.data || comparer(item, node.data) === 0);
                    isChecked && newCheckedItems.push(node.data);

                    if (self._param.isSimpleCheckMode) {
                        if (isChecked) {
                            checkedNode = [node];
                        } else {
                            node.check(false, {
                                stopEvent: true,
                                stopAffectChildren: true,
                                checkUnvisibleNode: true
                            });
                        }
                    } else {
                        if (isChecked) {
                            checkedNode.push(node);
                        } else {
                            node.check(false, {
                                stopEvent: true,
                                stopAffectChildren: true,
                                checkUnvisibleNode: true
                            });
                        }
                    }
                });
            };

            find(self._nodes);

            _(checkedNode).forEach(
                function (i) {
                    i.check(true, {
                        stopEvent: true,
                        stopAffectChildren: true,
                        checkUnvisibleNode: true,
                        stopAffectParent: checkParam && checkParam.stopAffectParent,
                        openParentOnCheck: checkParam && checkParam.openParentOnCheck
                    });
                }.bind(this)
            );

            // if oldOtem and newItems the same we don't need call onCheckedChanged
            const check =
                oldCheckedItems.length != newCheckedItems.length ||
                _(oldCheckedItems).some(function (i) {
                    return _(newCheckedItems).some(function (n) {
                        return customComparer ? customComparer(i, n) != 0 : self._param.comparer(i, n) != 0;
                    });
                });

            self = null;
            check && this._onCheckedChanged(oldCheckedItems, newCheckedItems);

            this._currentCheckedItems = newCheckedItems;

            // set this._initCheckedItems;
            if (newCheckedItems.length != checkedItems.length) {
                this._initCheckedItems = [];
                newCheckedItems.sort(this._param.comparer);
                _(checkedItems).forEach(
                    function (i) {
                        const check = this._binaryIndexOf(newCheckedItems, i) == -1;
                        check && this._initCheckedItems.push(i);
                    }.bind(this)
                );
            } else {
                this._initCheckedItems = [];
            }

            return newCheckedItems;
        }
    }

    nodes(nodes) {
        if (nodes) {
            this._nodes = nodes;
        } else {
            return this._nodes;
        }
    }

    last() {
        const length = this._nodes.length;
        return length ? this._nodes[length - 1] : null;
    }

    first() {
        return this._nodes[0] || null;
    }

    add(item) {
        const index = this._param.sortable ? -1 : this._items.length;
        return this.insert(index, item);
    }

    insert(index, item) {
        this._param.cloneItems && (item = commonUtils.clone(item));

        const oldSelectedItems = this._getSelectedItems();
        const oldCheckedItems = this._param.checkable ? this._getCheckedItems() : null;

        this._param.selectMode == selectMode.simple && item.selected && this._deselectAll();

        const node = this._createNode(item);

        if (index != -1) {
            this._nodes.splice(index, 0, node);
            this._items.splice(index, 0, item);
        } else {
            this._nodes.push(node);
            this._items.push(item);
            this._sortAll();

            _(this._items).forEach(function (i, ind) {
                if (i == item) {
                    index = ind;
                    return false;
                }
            });
        }

        this._onAdded(node, index);

        this._currentSelectedItems = this._getSelectedItems();
        this._currentCheckedItems = this._param.checkable ? this._getCheckedItems() : null;

        (oldSelectedItems.length != this._currentSelectedItems.length ||
            _(oldSelectedItems).some(i => {
                return _(this._currentSelectedItems).some(function (n) {
                    return n != i;
                });
            })) &&
            this._onSelectionChanged(oldSelectedItems, this._currentSelectedItems);

        if (this._param.checkable) {
            (oldCheckedItems.length != this._currentCheckedItems.length ||
                _(oldCheckedItems).some(i => {
                    return _(this._currentCheckedItems).some(function (n) {
                        return n != i;
                    });
                })) &&
                this._onCheckedChanged(oldCheckedItems, this._currentCheckedItems);
        }

        return node;
    }

    findNode(comparer) {
        let result;

        function find(items) {
            return _(items).forEach(function (node) {
                const check = comparer(node.data);

                if (check) {
                    result = node;
                    return false;
                } else if (node.children) {
                    find(node.children);
                }
            });
        }

        find(this._nodes);
        return result || null;
    }

    findNodeByItem(item) {
        let result;
        const self = this;

        function find(items) {
            return _(items).forEach(function (node) {
                const check = self._param.comparer(node.data, item) == 0;

                if (check) {
                    result = node;
                    return false;
                } else if (node.children) {
                    find(node.children);
                }
            });
        }

        find(this._nodes);
        return result || null;
    }

    findItem(comparer) {
        let result;

        function find(items) {
            return _(items).forEach(function (item) {
                const check = comparer(item);

                if (check) {
                    result = item;
                    return false;
                } else if (item.children) {
                    find(item.children);
                }
            });
        }

        find(this._items);
        return result || null;
    }

    findItems(comparer) {
        const result = [];

        function find(items) {
            return _(items).forEach(function (item) {
                const check = comparer(item);

                if (check) {
                    result.push(item);
                } else if (item.children) {
                    find(item.children);
                }
            });
        }

        find(this._items);
        return result || null;
    }

    selectNextNode(param) {
        const currentSelectedItems = this._getSelectedItems();

        if (currentSelectedItems.length && param && param.event && param.event.shiftKey) {
            currentSelectedItems.length == 1 && (this._goToTop = false);

            const item = !this._goToTop ? _(currentSelectedItems).last() : _(currentSelectedItems).first();

            let node = this.findNodeByItem(item);
            let ind;
            _(this._nodes).forEach(function (i, index) {
                if (i == node) {
                    ind = index;
                    return false;
                }
            });

            node = this._goToTop ? node : this._nodes[ind + 1];

            if (node) {
                node.select(node.selected ? false : true, {
                    stopEvent: true,
                    stopAffectParent: true,
                    stopAffectChildren: true,
                    deselectAll: false
                });
            }
        } else if (currentSelectedItems.length > 1) {
            this._shiftNode = null;
            const lastItem = _(currentSelectedItems).last();
            const lastNode = this.findNodeByItem(lastItem);

            this._deselectAll();

            let ind;
            _(this._nodes).forEach(function (i, index) {
                if (i == lastNode) {
                    ind = index;
                    return false;
                }
            });

            this._nodes[ind + 1] &&
                this._nodes[ind + 1].select(true, {
                    stopEvent: true,
                    stopAffectParent: true,
                    stopAffectChildren: true
                });
        } else {
            const setNext = function (items) {
                let n;
                _(items).forEach(
                    function (i, index) {
                        if (i.data.selected) {
                            n = i._next();
                        }

                        if (n) {
                            return false;
                        }

                        if (!n && i.children && i.children.length) {
                            n = setNext(i.children);

                            if (n) {
                                return false;
                            }
                        }
                    }.bind(this)
                );

                return n;
            };

            this._shiftNode = null;
            // select first node
            if (!currentSelectedItems.length) {
                const firstVisibleNode = _(this._nodes).find(function (n) {
                    return n.visibility() != false;
                });

                if (firstVisibleNode) {
                    this.selectedItems([firstVisibleNode.data]);
                    return firstVisibleNode.data;
                }
            } else {
                const n = setNext(this._nodes);
                if (!n) {
                    let n;
                    const find = items =>
                        _(items).some(function (i) {
                            return i.selected || find(i.children || []);
                        });
                    const root = _(this._nodes).find(function (i) {
                        return i.selected || find(i.children || []);
                    });

                    if (root) {
                        _(this._nodes).forEach(function (i, index) {
                            if (i == root) {
                                n = index;
                                return false;
                            }
                        });

                        for (let i = n + 1; i < this._nodes.length; i++) {
                            if (this._nodes[i].visibility() != false) {
                                this.selectedItems([this._nodes[i].data]);
                                return this._nodes[i].data;
                            }
                        }
                    }
                } else {
                    this.selectedItems([n.data]);
                    return n.data;
                }
            }
        }
    }

    selectPrevNode(param) {
        const currentSelectedItems = this._getSelectedItems();
        let lastNode;

        if (currentSelectedItems.length && param && param.event && param.event.shiftKey) {
            currentSelectedItems.length == 1 && (this._goToTop = true);

            const item = this._goToTop || currentSelectedItems.length == 1 ? _(currentSelectedItems).first() : _(currentSelectedItems).last();

            let node = this.findNodeByItem(item);

            let ind;
            _(this._nodes).forEach(
                function (i, index) {
                    if (i == node) {
                        ind = index;
                        return false;
                    }
                }.bind(this)
            );

            node = this._goToTop ? this._nodes[ind - 1] : node;

            if (node) {
                node.select(node.selected && currentSelectedItems.length != 1 ? false : true, {
                    stopEvent: true,
                    stopAffectParent: true,
                    stopAffectChildren: true,
                    deselectAll: false
                });

                this._lastShiftEvent = {
                    isPrev: true,
                    node: this._nodes[ind - 1]
                };
            }
        } else if (currentSelectedItems.length > 1) {
            this._shiftNode = null;
            const firstItem = _(currentSelectedItems).first();
            const firstNode = this.findNodeByItem(firstItem);

            this._deselectAll();

            let ind;
            _(this._nodes).forEach(function (i, index) {
                if (i == lastNode) {
                    ind = index;
                    return false;
                }
            });

            this._nodes[ind - 1] &&
                this._nodes[ind - 1].select(true, {
                    stopEvent: true,
                    stopAffectParent: true,
                    stopAffectChildren: true
                });
        } else {
            const setPrev = function (items) {
                let n;
                _(items).forEach(
                    function (i, index) {
                        if (i.data.selected) {
                            n = i._prev();
                        }

                        if (n) {
                            return false;
                        }

                        if (!n && i.children && i.children.length) {
                            n = setPrev(i.children);

                            if (n) {
                                return false;
                            }
                        }
                    }.bind(this)
                );

                return n;
            };

            this._shiftNode = null;
            if (currentSelectedItems.length) {
                const n = setPrev(this._nodes);
                if (!n) {
                    let n;
                    const find = items =>
                        _(items).some(function (i) {
                            return i.selected || find(i.children || []);
                        });
                    const root = _(this._nodes).find(function (i) {
                        return i.selected || find(i.children || []);
                    });

                    if (root) {
                        _(this._nodes).forEach(function (i, index) {
                            if (i == root) {
                                n = index;
                                return false;
                            }
                        });

                        for (let i = n - 1; i >= 0; i--) {
                            if (this._nodes[i].visibility() != false) {
                                const visibleChild = _(this._nodes[i].children)
                                    .reverse()
                                    .find(function (i) {
                                        return i.visibility() !== false;
                                    });

                                if (this._nodes[i].expanded && visibleChild) {
                                    this.selectedItems([visibleChild.data]);
                                    return visibleChild.data;
                                } else {
                                    this.selectedItems([this._nodes[i].data]);
                                    return this._nodes[i].data;
                                }
                            }
                        }
                    }
                } else {
                    this.selectedItems([n.data]);
                }
            }
        }
    }

    expandedItems(expandedItems) {
        function find(items) {
            _(items).forEach(function (node) {
                if (node.hasChildren) {
                    if (node.expanded) {
                        !_(expandedItems).some(function (i) {
                            return i === node;
                        }) && node.collapse({ needAdjust: false });
                        find(node.children);
                    }
                }
            });
        }

        find(this._nodes);
    }

    setNodesVisibility(compareItems, param) {
        this._currentSelectedItems = this._getSelectedItems();

        if (this._param.visibilityComparer) {
            const newSelectionItems = [];
            let checkVisibleChanged = false;
            !compareItems && (compareItems = this._nodes);
            let self = this;
            const visibilityComparer = items => {
                let v = false;

                _(items).forEach(function (i) {
                    const childVisibility = visibilityComparer(i.children);
                    const compareValue = self._param.visibilityComparer(i.data);

                    checkVisibleChanged = !(compareValue || childVisibility) || checkVisibleChanged; //'//' && i.selected;
                    i.visibility(compareValue || childVisibility);

                    if ((compareValue || childVisibility) && param && param.openChildren && i.hasChildren) {
                        i.expand({ needAdjust: false });
                    }

                    const hideArrow =
                        i.data.dynamic && !i.data.dinamicIsLoaded
                            ? compareValue && !childVisibility && i.dinamicIsLoaded === true
                            : compareValue && !childVisibility;

                    hideArrow ? i.hideChildren() : i.showChildren();

                    v = v || compareValue || childVisibility;

                    if (i.data.selected && (compareValue || childVisibility)) {
                        newSelectionItems.push(i.data);
                    }
                });

                return v;
            };

            visibilityComparer(compareItems);
            if (checkVisibleChanged) {
                this.selectedItems(newSelectionItems);
            }
            self = null;
            /*if(self.param.checkable &&
               self.currentCheckedItems.length &&
               newCheckedItems.length != self.currentCheckedItems.length){

                self.checkedItems(newCheckedItems);
            }*/
        }
    }

    setCheckBoxVisibility(compareItems, param) {
        function find(nodes) {
            _(nodes).forEach(function (node) {
                const checkVisibility = self._param.checkBoxVisibilityComparer(node.data);
                node.checkable(checkVisibility);
                node.children && node.children.length && find(node.children);
            });
        }

        let self = this;

        if (this._param.checkBoxVisibilityComparer) {
            find(compareItems || this._nodes);
        }
        self = null;
    }

    updateNode(id, item) {
        const node = this.findNode(data => data.id == id);
        node && node.update(item);

        for (const currentItem of treeIterator(this._items, i => i.children || [])) {
            if (currentItem.id == id) {
                for (const key of Object.keys(item)) {
                    safeAssign(currentItem, key, item[key]);
                }
            }
        }
    }

    getOpenedNodes() {
        const openNodes = [];

        function find(items) {
            _(items).forEach(function (i) {
                i.expanded && openNodes.push(i);
                i.expanded && find(i.children || []);
            });
        }

        find(this._nodes);
        return openNodes;
    }

    compareItems(a, b) {
        return a == b || this._param.comparer(a, b) == 0;
    }

    destroy() {
        this.forEachNode(function (node) {
            node.destroy();
        });

        this._param = null;
        this._items = null;
        this._nodes = null;
        this._currentSelectedItems = null;
        this._currentCheckedItems = null;
        this._initCheckedItems = null;
        this._initSelectedItems = null;

        destroyEvents(this);
    }

    // Events
    onSelectionChanged(fn) {
        this._onSelectionChanged = fn;
    }

    onCheckedChanged(fn) {
        this._onCheckedChanged = fn;
    }

    onAdded(fn) {
        this._onAdded = fn;
    }

    forEachNode(fn) {
        function forEach(items) {
            _(items).forEach(function (i) {
                fn(i);
                forEach(i.children || []);
            });
        }

        forEach(this._nodes);
    }
}

// TreeNode
class TreeNode {
    private _onVisibilityChanges: () => void;
    private _onCollapse: (param) => void;
    private _onExpand: (param?, ready?) => void;
    private _onHideChildren: () => void;
    private _onShowChildren: () => void;
    private _param: any;
    private _item: any;
    private _level: any;
    private _children: any;
    private _parent: any;
    private _selected: any;
    private _checked: any;
    private _expanded: any;
    private _visibility: any;
    private _checkable: any;

    constructor(item, param) {
        this._onVisibilityChanges = $.noop;
        this._onCollapse = $.noop;
        this._onExpand = $.noop;
        this._onHideChildren = $.noop;
        this._onShowChildren = $.noop;

        this._param = {
            ...{
                isUpdate: false,
                level: 0,
                isRoot: true,

                checkable: false,
                selectMode: selectMode.simple,
                highlightAllSelectedWay: false,
                comparer: undefined,
                allowSorting: false,
                sortable: true,
                visibilityComparer: null,
                idPropertyName: 'id',
                namePropertyName: 'name'
            },
            ...param
        };

        this._item = item || {};
        this._level = this._param.level;
        this._children = undefined;
        this._parent = param && param.parentNode;
        this._selected = item && item.selected;
        this._checked = item && item.checked;
        this._expanded = item && item.expanded;
        this._visibility = item && item.visibility;
        this._checkable = item && item.checkable;

        if (item && item.children && item.children.length) {
            this._addChildren(item.children);
        }
    }

    // Private function
    private _sortAll() {
        let self = this;
        if (self._param.folderOnTop && this._item.folderOnTop !== false) {
            const parentToTop = (items, nodeParam?) => {
                const folders = [];
                const notFolders = [];

                _(items).forEach(function (item) {
                    let i;
                    if (nodeParam && nodeParam.isNode) {
                        i = item.data;
                    } else {
                        i = item;
                    }

                    if (i.isFolder === true || (_.isFunction(i.isFolder) && i.isFolder())) {
                        folders.push(item);
                    } else {
                        notFolders.push(item);
                    }
                });

                if (self._param.sortable && self._item.sortable !== false) {
                    if (nodeParam && nodeParam.isNode) {
                        self._param.sort
                            ? folders.sort(
                                  function (a, b) {
                                      return self._param.sort(a.data, b.data);
                                  }.bind(self)
                              )
                            : folders.sort(self._sortNode(self._param.namePropertyName));

                        self._param.sort
                            ? notFolders.sort(
                                  function (a, b) {
                                      return self._param.sort(a.data, b.data);
                                  }.bind(self)
                              )
                            : notFolders.sort(self._sortNode(self._param.namePropertyName));
                    } else {
                        self._param.sort ? folders.sort(self._param.sort) : folders.sort(self._sortItem(self._param.namePropertyName));

                        self._param.sort ? notFolders.sort(self._param.sort) : notFolders.sort(self._sortItem(self._param.namePropertyName));
                    }
                }

                return folders.concat(notFolders);
            };

            if (self.data._children && self.data._children) {
                self.data._children = parentToTop(self.data._children);
            }
            if (self._children && self._children.length) {
                self._children = parentToTop(self._children, { isNode: true });
            }
        } else if (self._param.sortable && this._item.sortable !== false) {
            if (self.data._children && self.data._children) {
                self._param.sort
                    ? self.data._children.sort(self._param.sort)
                    : self.data._children.sort(self._sortItem(self._param.namePropertyName));
            }

            if (self._children && self._children.length) {
                self._param.sort
                    ? self._children.sort(
                          function (a, b) {
                              return self._param.sort(a.data, b.data);
                          }.bind(self)
                      )
                    : self._children.sort(self._sortNode(self._param.namePropertyName));
            }
        }

        self = null;
    }

    private _sortItem(prop) {
        return function (a, b) {
            return comparers.stringComparer(a[prop], b[prop]);
        };
    }

    private _sortNode(prop) {
        return function (a, b) {
            return comparers.stringComparer(a.data[prop], b.data[prop]);
        };
    }

    private _addChildren(items, addChildrenParam?) {
        let self = this;
        if (self._param.folderOnTop && this._item.folderOnTop !== false) {
            const parentToTop = items => {
                const folders = [];
                const notFolders = [];

                _(items).forEach(function (item) {
                    if (item.isFolder) {
                        folders.push(item);
                    } else if (item.isFolder === undefined && item.children !== undefined) {
                        folders.push(item);
                    } else {
                        notFolders.push(item);
                    }
                });

                if (self._param.sortable && self._item.sortable !== false) {
                    self._param.sort ? folders.sort(self._param.sort) : folders.sort(self._sortItem(self._param.namePropertyName));

                    self._param.sort ? notFolders.sort(self._param.sort) : notFolders.sort(self._sortItem(self._param.namePropertyName));
                }

                return folders.concat(notFolders);
            };

            items = parentToTop(items);
        } else if (self._param.sortable && this._item.sortable !== false) {
            self._param.sort ? items.sort(self._param.sort) : items.sort(self._sortItem(self._param.namePropertyName));
        }

        _(items).forEach(i => {
            this.add(i, addChildrenParam);
        });

        self = null;
    }

    private _next() {
        let n;

        const visibleChild = _(this.children).find(function (i) {
            return i.visibility() !== false;
        });

        if (
            !_(this.children).some(function (i) {
                return i.selected;
            }) &&
            this.children &&
            this.children.length &&
            visibleChild
        ) {
            return visibleChild;
        } else if (this._parent && this._parent.children && this._parent.children.length) {
            _(this._parent.children).forEach(
                function (i, index) {
                    if (i == this) {
                        n = index;
                        return false;
                    }
                }.bind(this)
            );

            for (let i = n + 1; i < this._parent.children.length; i++) {
                if (this._parent.children[i].visibility() != false) {
                    return this._parent.children[i];
                    break;
                }
            }

            return this._parent._next();
        }
    }

    private _prev() {
        let n;
        if (this._parent && this._parent.children && this._parent.children.length) {
            _(this._parent.children).forEach(
                function (i, index) {
                    if (i == this) {
                        n = index;
                        return false;
                    }
                }.bind(this)
            );

            for (let i = n - 1; i >= 0; i--) {
                if (this._parent.children[i].visibility() != false) {
                    return this._parent.children[i];
                    break;
                }
            }

            if (this._parent && this._parent.visibility() != false) {
                return this._parent;
            } else {
                return this._parent._next();
            }
        }
    }

    // Public function
    clear() {
        this._children = [];
        _.set(this.data, 'children', []); // children - can be only getter defined
        this.onUpdate(this.data);
        this._expanded && this.collapse();
    }

    clearDynamic() {
        this._children = [];
        this.data.children = [];

        this.data.dynamic && this.data.dinamicIsLoaded && (this.data.dinamicIsLoaded = false);

        this.onUpdate(this.data);
        this._expanded && this.collapse();
    }

    // TreeNode.prototype.items = function (items){
    //
    //     this._param.cloneItems &&
    //         (items = commonUtils.clone(items));
    //
    //     if (items === undefined){
    //
    //         return _.map(this.children, function (i: {data}){ return i.data; });
    //     }
    //
    //     _(items).forEach(function (i){ this.add(i); }.bind(this));
    // };
    nodes(nodes) {
        if (nodes === undefined) {
            return this.children;
        }

        this._children = nodes;
        this._onUpdate();
    }

    add(data, addParam) {
        const index = this._param.sortable ? -1 : (this._children && this._children.length) || 0;
        return this.insert(index, data, addParam);
    }

    insert(index, data, insertParam) {
        // create fake index
        if ((data[this._param.idPropertyName] === undefined || data[this._param.idPropertyName] === null) && this._param.fakeIdIndex) {
            data._innerId = this._param.fakeIdIndex.value++;
        }

        this._param.cloneItems && (data = commonUtils.clone(data));

        const param = {
            ...{},
            ...this._param,
            ...{
                isUpdate: false,
                level: this._param.level + 1,
                isRoot: false,
                parentNode: this
            }
        };

        const node = new TreeNode(data, param);
        node._onRemove = function (data) {
            if (this._parent) {
                this._parent._onRemove(data);
            } else {
                this._onRemove(data);
            }
        };

        node._onSelect = function (data, value, selectParam) {
            if (this._parent) {
                this._parent._onSelect(data, value, selectParam);
            }
        };

        node._onCheck = function (data, value, checkParam) {
            if (this._parent) {
                this._parent._onCheck(data, value, checkParam);
            }
        };

        node._onPreCheck = function (data, value) {
            if (this._parent) {
                return this._parent._onPreCheck(data, value);
            }
        };

        node._onUpdate = function (data, value) {
            if (this._parent) {
                this._parent._onUpdate(data, value);
            } else {
                this._onUpdate(data, value);
            }
        };

        if (!this._children || !this._children.length) {
            this._children = [];
            this._children.push(node);
            safeAssign(this._item, 'children', [data]);
        } else {
            if (index != -1) {
                this._children.splice(index, 0, node);
                !_(this._item.children).some(function (i) {
                    return i == data;
                }) && this._item.children.splice(index, 0, data);
            } else {
                this._children.push(node);
                !_(this._item.children).some(function (i) {
                    return i == data;
                }) && this._item.children.push(data);

                this._sortAll();

                _(this._item.children).forEach(function (i, ind) {
                    if (i == data) {
                        index = ind;
                        return false;
                    }
                });
            }
        }

        if (!insertParam || !insertParam.stopEvent) {
            this.onUpdate(this.data, { index: index });
        }

        data.selected && node.select(true);
        data.checked && node.check(true, { stopEvent: true, stopAffectChildren: true, checkUnvisibleNode: true });

        return node;
    }

    remove() {
        if (this._parent) {
            this._parent._onRemove(this._item);
        } else {
            this._onRemove(this._item);
        }

        this.onRemove(this._item);

        if (this._parent) {
            if (!this._parent.children || !this._parent.children.length) {
                this._parent.collapse();
            }
        }
    }

    expandAllParent() {
        if (this.hasChildren || this.data.dynamic) {
            this._expanded = true;
            this._onExpand();
        }

        this.parent && this.parent.expand();
    }

    expand(param?, ready?) {
        if (this.hasChildren || this.data.dynamic) {
            this._expanded = true;
            this._onExpand(param, ready);
        }
    }

    collapse(param?) {
        this._expanded = false;
        this._onCollapse(param);
    }

    update(data, updateWithoutChildren) {
        if (!updateWithoutChildren) {
            for (const key of Object.keys(data)) {
                safeAssign(this._item, key, data[key]);
            }

            this._children = undefined;
            if (data.children && data.children.length) {
                this._addChildren(data.children, { stopEvent: true });
            }
        }

        if (this._parent) {
            this._parent._onUpdate(this._item, data);
        } else {
            this._onUpdate(this._item, data);
        }

        this._parent && this._parent._sortAll();
        this.onUpdate(data);
    }

    select(data, selectParam?) {
        if ((selectParam && selectParam.event && selectParam.event.shiftKey) || !selectParam || !selectParam.event || this._selected != data) {
            if (this._parent) {
                this._parent._onSelect(this, data === true, selectParam);
            } else {
                this._onSelect(this, data === true, selectParam);
            }

            this.onSelect(data === true);
        }

        this._selected = data;
        this._item.selected = data;

        /*if (this._param.checkOnSelect && !this._item.dynamic && !this.hasChildren){

            this.check(data);
        }*/
    }

    check(data, checkParam) {
        let oldCheckedItems;

        if (!checkParam || !checkParam.stopEvent) {
            oldCheckedItems = this._parent ? this._parent._onPreCheck() : this._onPreCheck();
        }

        // check children
        if ((!checkParam || !checkParam.stopAffectChildren) && !this._param.isSimpleCheckMode && this._children && this._children.length) {
            _(this._children).forEach(function (i) {
                (i._visibility != false || (checkParam && checkParam.checkUnvisibleNode)) &&
                    i.check(data, $.extend({ stopAffectParent: true, stopEvent: true }, checkParam));
            });
        }

        // check parent
        if (!checkParam || !checkParam.stopAffectParent) {
            // keep check off hierarchy
            if (this.parent && data && !this._param.isSimpleCheckMode) {
                this.parent.check(true, { stopAffectChildren: true, stopEvent: true });
            } else if (this.parent && !data && !this._param.isSimpleCheckMode && this._param.keepCheckoffHierarchy) {
                const anyChildrenChecked = _(this.parent.children).some(function (i) {
                    return i.checked === true;
                });
                this.parent.checked != anyChildrenChecked &&
                    this.parent.check(anyChildrenChecked, {
                        stopAffectChildren: true,
                        stopEvent: true
                    });
            }

            // open parent on check
            if (((checkParam && checkParam.openParentOnCheck) || this._param.openParentOnCheck) && this.parent) {
                this.parent.expand();
            }
        }

        this._parent
            ? this._parent._onCheck(this._item, data === true, $.extend({ oldCheckedItems: oldCheckedItems }, checkParam))
            : this._onCheck(this._item, data === true, $.extend({ oldCheckedItems: oldCheckedItems }, checkParam));

        if (!(this._checked === undefined && data === false) && this._checked != data) {
            this._checked = data;
            this._item.checked = data;

            checkParam && checkParam.clientClick && (checkParam.clientCheck = false);
            this.onCheck(data === true, checkParam);
        }
    }

    visibility(data) {
        if (data === undefined) {
            return this._visibility;
        } else {
            this._visibility = data;
            this._item.visibility = data;
            this.onVisibilityChanges();
        }
    }

    checkable(data) {
        if (data === undefined) {
            return this._checkable;
        } else {
            this._checkable = data;
            this._item.checkable = data;
            this.onCheckableChanges();
        }
    }

    onCheckableChanges() {
        throw new Error('Method not implemented.');
    }

    ensureVisible() {
        this.expand();
        this.parent && this.parent.ensureVisible();
    }

    hideChildren(data) {
        this._onHideChildren();
    }

    showChildren(data) {
        this._onShowChildren();
    }

    destroy() {}

    // Events
    _onSelect(data, value, selectParam) {}

    _onRemove(data) {}

    _onCheck(data, value, checkParam) {}

    _onPreCheck(data?, value?) {}

    _onUpdate(data?, value?) {}

    onSelect(b: boolean) {}

    onCheck(b, checkParam) {}

    onRemove(item) {}

    onUpdate(data, param?: { index: number }) {}

    onExpand(fn) {
        this._onExpand = fn;
    }

    onCollapse(fn) {
        this._onCollapse = fn;
    }

    onHideChildren(fn) {
        this._onHideChildren = fn;
    }

    onShowChildren(fn) {
        this._onShowChildren = fn;
    }

    onVisibilityChanges() {}

    // get
    get param() {
        return this._param;
    }

    get data() {
        return this._item;
    }

    get level() {
        return this._level;
    }

    get children() {
        return this._children;
    }

    get hasChildren() {
        return this._item.children && this._item.children.length;
    }

    get parent() {
        return this._parent || null;
    }

    get selected() {
        return this._item && this._item.selected;
    }

    get checked() {
        return this._checked;
    }

    get expanded() {
        return this._expanded || false;
    }
}

function safeAssign(obj: {}, key: string, value: any): void {
    try {
        obj[key] = value;
    } catch {}
}
